import React, { Fragment } from "react";

import { useTranslation } from "utils";

import { CATEGORY_ORDER, FACTOR_UI, type FactorCategoryKey, type FactorUiDef, type ImpactLeafFactorKey, SUB_FACTOR_ORDER } from "consts";

import { Icon, Tooltip, Typography } from "components";

import { formatSignedPercent, valueToneClassName } from "pages/app/scenario-analysis/utils/comparisonFormatting";
import type { ViewMode } from "pages/app/scenario-analysis/utils/forecast";
import { categoryTotal } from "pages/app/scenario-analysis/utils/impactFactors";

function FactorTooltipContent({ title, body }: { title: string; body: string }) {
  return (
    <div className="outcome-comparison-impact-tooltip">
      <div className="outcome-comparison-impact-tooltip-title">{title}</div>
      <div className="outcome-comparison-impact-tooltip-body">{body}</div>
    </div>
  );
}

/** The table's trailing expand column; shows a chevron only on rows that can expand. */
export const ExpandCell = ({ chevron }: { chevron?: { expanded: boolean } }) => (
  <td className="outcome-comparison-table-cell outcome-comparison-table-expand">
    {chevron ? (
      <Icon
        className={`outcome-comparison-table-chevron ${chevron.expanded ? "outcome-comparison-table-chevron--expanded" : ""}`}
        iconName="tabler/chevron-down"
        size="s"
      />
    ) : null}
  </td>
);

/** Rows shown above the impact categories; each is also a leaf total. */
type StructuralFactorKey = Extract<ImpactLeafFactorKey, "regular_projection" | "market_price_trend" | "input_cost_trend">;

type FactorRowsProps = {
  scenarioId: string;
  /** The scenario's line colour, repeated on every breakdown row's dot. */
  lineColor: string;
  displayYears: number[];
  allYears: number[];
  /** This scenario's factor-leaf totals per year. */
  leafTables: Record<number, Record<ImpactLeafFactorKey, number>> | undefined;
  /** Whether the API reported this factor; erosion and water holding can be left out (irrigation model, or CM breakdown flag on). */
  isFactorReported: (factorKey: ImpactLeafFactorKey) => boolean;
  /** The CM breakdown flag; switches descriptions that name a factor it removes. */
  isCmBreakdownEnabled: boolean;
  viewMode: ViewMode;
  showTotalCol: boolean;
  marketPricePct: number;
  costDevelopmentPct: number;
  forceExpandAll: boolean;
  expandedCategories: Set<string>;
  onToggleCategory: (key: string) => void;
  formatCell: (value: number) => string;
  dataTestId: string;
};

/** An expanded scenario's breakdown: the structural rows, then each impact category with its sub-factors. */
export const FactorRows = ({
  scenarioId,
  lineColor,
  displayYears,
  allYears,
  leafTables,
  isFactorReported,
  isCmBreakdownEnabled,
  viewMode,
  showTotalCol,
  marketPricePct,
  costDevelopmentPct,
  forceExpandAll,
  expandedCategories,
  onToggleCategory,
  formatCell,
  dataTestId,
}: FactorRowsProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const descriptionKeyOf = (def: FactorUiDef) => (isCmBreakdownEnabled && def.i18nCmBreakdownDescriptionKey) || def.i18nDescriptionKey;

  // Sum of leaf values across all years for annual Total column breakdown
  const getBreakdownTotal = (leafKey: ImpactLeafFactorKey): number => allYears.reduce((sum, y) => sum + (leafTables?.[y]?.[leafKey] ?? 0), 0);

  const renderStructuralRow = (factorKey: StructuralFactorKey) => {
    const def = FACTOR_UI[factorKey];
    const labelKey = `outcomeComparison.impactFactors.${def.i18nLabelKey}`;
    const descKey = `outcomeComparison.impactFactors.${descriptionKeyOf(def)}`;
    const label =
      factorKey === "market_price_trend"
        ? t(labelKey, { pct: formatSignedPercent(marketPricePct) })
        : factorKey === "input_cost_trend"
        ? t(labelKey, { pct: formatSignedPercent(costDevelopmentPct) })
        : t(labelKey);

    return (
      <tr
        key={`${scenarioId}-${factorKey}`}
        className="outcome-comparison-table-row outcome-comparison-table-row--breakdown structural"
        data-testid={`${dataTestId}-breakdown-${scenarioId}-${factorKey}`}
      >
        <td className="outcome-comparison-table-name outcome-comparison-table-name-sticky outcome-comparison-table-name-nested">
          <span className="outcome-comparison-table-name-indent" aria-hidden />
          <span className="outcome-comparison-table-chevron-slot" aria-hidden />
          <span className="outcome-comparison-table-dot" style={{ backgroundColor: lineColor }} aria-hidden />
          <Typography variant="paragraph-m">{label}</Typography>
          <Tooltip title={<FactorTooltipContent title={label} body={t(descKey)} />} placement="top" variant="neutral">
            <span className="outcome-comparison-impact-info" aria-label={label}>
              <Icon iconName="tabler/info-circle" size="s" />
            </span>
          </Tooltip>
        </td>
        {displayYears.map((y) => {
          const leaf = leafTables?.[y];
          const v = leaf ? leaf[factorKey] : 0;
          return (
            <td key={y} className="outcome-comparison-table-cell">
              <div className="outcome-comparison-table-cell-inner">
                <span className={`outcome-comparison-table-value outcome-comparison-table-value--detail ${valueToneClassName(v)}`}>{formatCell(v)}</span>
              </div>
            </td>
          );
        })}
        {showTotalCol ? (
          <td className="outcome-comparison-table-cell outcome-comparison-table-cell--total">
            <div className="outcome-comparison-table-cell-inner">
              <span className={`outcome-comparison-table-value outcome-comparison-table-value--detail ${valueToneClassName(getBreakdownTotal(factorKey))}`}>
                {formatCell(getBreakdownTotal(factorKey))}
              </span>
            </div>
          </td>
        ) : null}
        <ExpandCell />
      </tr>
    );
  };

  const renderCategoryRow = (cat: FactorCategoryKey) => {
    // In yield mode, only show yield_quality category
    if (viewMode === "yield" && cat !== "yield_quality") return null;

    const fk = cat;
    const def = FACTOR_UI[fk];
    const catUiKey = `${scenarioId}-${fk}`;
    const catOpen = forceExpandAll || expandedCategories.has(catUiKey);
    const label = t(`outcomeComparison.impactFactors.${def.i18nLabelKey}`);
    const descBody = t(`outcomeComparison.impactFactors.${descriptionKeyOf(def)}`);

    // Sub-factors to show in yield mode breakdown
    const yieldSubFactors: ImpactLeafFactorKey[] = ["yield_amount", "quality_improvement"];
    const subFactors = viewMode === "yield" ? yieldSubFactors : SUB_FACTOR_ORDER[cat].filter(isFactorReported);

    return (
      <Fragment key={`${scenarioId}-cat-${cat}`}>
        <tr
          className={`outcome-comparison-table-row outcome-comparison-table-row--breakdown category ${catOpen ? "is-expanded" : ""}`}
          data-testid={`${dataTestId}-breakdown-${scenarioId}-${cat}`}
          onClick={() => onToggleCategory(catUiKey)}
          aria-expanded={catOpen}
        >
          <td className="outcome-comparison-table-name outcome-comparison-table-name-sticky outcome-comparison-table-name-nested">
            <span className="outcome-comparison-table-name-indent outcome-comparison-table-name-indent-category" aria-hidden />
            <Icon
              className={`outcome-comparison-table-chevron ${catOpen ? "outcome-comparison-table-chevron--expanded" : ""}`}
              iconName="tabler/chevron-down"
              size="s"
            />
            <span className="outcome-comparison-table-dot outcome-comparison-table-dot-accent" style={{ backgroundColor: lineColor }} aria-hidden />
            <Typography variant="paragraph-m" className="outcome-comparison-table-category-label">
              {label}
            </Typography>
            <Tooltip title={<FactorTooltipContent title={label} body={descBody} />} placement="top" variant="neutral">
              <span className="outcome-comparison-impact-info" aria-label={label} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <Icon iconName="tabler/info-circle" size="s" />
              </span>
            </Tooltip>
          </td>
          {displayYears.map((y) => {
            const leaf = leafTables?.[y];
            const v = leaf ? categoryTotal(leaf, cat) : 0;
            return (
              <td key={y} className="outcome-comparison-table-cell" onClick={(e) => e.stopPropagation()}>
                <div className="outcome-comparison-table-cell-inner">
                  <span className={`outcome-comparison-table-value outcome-comparison-table-value--detail ${valueToneClassName(v)}`}>{formatCell(v)}</span>
                </div>
              </td>
            );
          })}
          {showTotalCol ? (
            <td className="outcome-comparison-table-cell outcome-comparison-table-cell--total" onClick={(e) => e.stopPropagation()}>
              <div className="outcome-comparison-table-cell-inner">
                {(() => {
                  const total = allYears.reduce((sum, y) => sum + (leafTables?.[y] ? categoryTotal(leafTables[y], cat) : 0), 0);
                  return (
                    <span className={`outcome-comparison-table-value outcome-comparison-table-value--detail ${valueToneClassName(total)}`}>
                      {formatCell(total)}
                    </span>
                  );
                })()}
              </div>
            </td>
          ) : null}
          <ExpandCell />
        </tr>
        {catOpen
          ? subFactors.map((subKey) => {
              const subFactorUi = FACTOR_UI[subKey];
              const subLabel = t(`outcomeComparison.impactFactors.${subFactorUi.i18nLabelKey}`);
              const subDesc = t(`outcomeComparison.impactFactors.${descriptionKeyOf(subFactorUi)}`);

              return (
                <tr
                  key={`${scenarioId}-sub-${subKey}`}
                  className="outcome-comparison-table-row outcome-comparison-table-row--breakdown leaf"
                  data-testid={`${dataTestId}-breakdown-${scenarioId}-${subKey}`}
                >
                  <td className="outcome-comparison-table-name outcome-comparison-table-name-sticky outcome-comparison-table-name-nested">
                    <span className="outcome-comparison-table-name-indent outcome-comparison-table-name-indent-leaf" aria-hidden />
                    <span className="outcome-comparison-table-dot outcome-comparison-table-dot-leaf" style={{ backgroundColor: lineColor }} aria-hidden />
                    <Typography variant="paragraph-s">{subLabel}</Typography>
                    <Tooltip title={<FactorTooltipContent title={subLabel} body={subDesc} />} placement="top" variant="neutral">
                      <span className="outcome-comparison-impact-info" aria-label={subLabel}>
                        <Icon iconName="tabler/info-circle" size="xs" />
                      </span>
                    </Tooltip>
                  </td>
                  {displayYears.map((y) => {
                    const leaf = leafTables?.[y];
                    const v = leaf ? leaf[subKey] : 0;
                    return (
                      <td key={y} className="outcome-comparison-table-cell">
                        <div className="outcome-comparison-table-cell-inner">
                          <span className={`outcome-comparison-table-value outcome-comparison-table-value--detail ${valueToneClassName(v)}`}>
                            {formatCell(v)}
                          </span>
                        </div>
                      </td>
                    );
                  })}
                  {showTotalCol ? (
                    <td className="outcome-comparison-table-cell outcome-comparison-table-cell--total">
                      <div className="outcome-comparison-table-cell-inner">
                        <span
                          className={`outcome-comparison-table-value outcome-comparison-table-value--detail ${valueToneClassName(getBreakdownTotal(subKey))}`}
                        >
                          {formatCell(getBreakdownTotal(subKey))}
                        </span>
                      </div>
                    </td>
                  ) : null}
                  <ExpandCell />
                </tr>
              );
            })
          : null}
      </Fragment>
    );
  };

  return (
    <>
      {renderStructuralRow("regular_projection")}
      {viewMode === "cumulative" && marketPricePct !== 0 ? renderStructuralRow("market_price_trend") : null}
      {viewMode === "cumulative" && costDevelopmentPct !== 0 ? renderStructuralRow("input_cost_trend") : null}
      {CATEGORY_ORDER.map(renderCategoryRow)}
    </>
  );
};
